<template>
  <NoMountingSelectorExchange
    ref="noMountingSelectorRef"
    v-model:is-exchange-possible="isExchangePossible"
    v-model:failed-exchange-attempt-reason="TKDService.deviceService.reason"
    v-model:failed-exchange-attempt-note="TKDService.deviceService.note"
    :installation-point="installationPoint"
  />

  <BaseSection v-if="!isExchangePossible" heading="Adapter" :required="false">
    <AccessoryList ref="accessoryListRef" v-model="installationPoint" />
  </BaseSection>

  <DeviceVerificationForm
    v-if="isExchangePossible"
    ref="deviceVerificationFormRef"
    v-model:device="oldDevice"
    v-model:device-event="oldDeviceEvent"
    v-model:installation-point="installationPoint"
    v-model:tkd-service="TKDService"
  />

  <SmartRadiatorThermostatForm
    v-if="
      deviceType &&
      installationPoint &&
      device &&
      deviceEvent &&
      isExchangePossible
    "
    ref="formComponentRef"
    v-model:device="currentDevice"
    v-model:device-event="deviceEvent"
    v-model:installation-point="installationPoint"
    v-model:new-battery-pack="newBatteryPack"
    data-testid="deviceForm"
    @close-and-save-form="handleSubmit"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, provide, ref, Ref, unref, watch } from 'vue';
import { useDeviceForm } from '@/composables/useDeviceForm';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { useOldDeviceForm } from '@/composables/useOldDeviceForm';
import { DeviceType } from '@/models/devices/Device';
import { DeviceServiceTypeEnum } from '@/enums/device';
import { FormAction, useFormStore } from '@/store/form/formStore';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import AccessoryList from '@/components/Forms/DeviceFormComponents/AccessoryList.vue';
import { useTKDService } from '@/composables/useTKDService';
import DeviceVerificationForm from '@/components/Forms/TkdServiceComponents/DeviceVerificationForm.vue';
import NoMountingSelectorExchange from '@/components/Forms/DeviceFormComponents/NoMountingSelectorExchange.vue';
import SmartRadiatorThermostatForm from '@/components/Forms/DeviceFormComponents/DeviceTypeForms/SmartRadiatorThermostatForm.vue';

const props = defineProps<{
  node?: InstallationPoint;
  parentId: string;
  payload?: any;
}>();
const emit = defineEmits(['update-entity', 'update-entities']);

const {
  device,
  installationPoint,
  deviceEvent,
  failedInstallationAttempt,
  initDevice,
  initAccessories,
  initInstallationPoint,
  updateDevice: updateNewDevice,
} = useDeviceForm(emit);

const {
  device: oldDevice,
  deviceEvent: oldDeviceEvent,
  initOldDevice: initOldDevice,
} = useOldDeviceForm(emit);

const {
  initService,
  TKDDevice,
  newBatteryPack,
  TKDService,
  handleSubmit: handleSubmitTkdService,
} = useTKDService(installationPoint);

const currentDevice = computed(() => {
  if (isReplaceAction.value) {
    return TKDDevice.value;
  }
  return device.value;
});

const deviceType = ref(props.payload?.deviceType);
const hasOldDevice = ref(false);
const isExchangePossible = ref(true);

const isReplaceAction = computed(
  () => useFormStore().formAction === FormAction.REPLACE
);

onBeforeMount(() => {
  if (!props.node) {
    throw new Error('Installation point is not initialized');
  }
  deviceType.value = props.node.supportedDeviceType;
  initInstallationPoint(props.parentId, props.node);
  initNewDevice(deviceType.value, props.node);

  if (props.node.activeDeviceId) {
    const activeDevice = props.node.getActiveDevice();
    hasOldDevice.value = true;
    initOldDevice(deviceType.value, props.node, activeDevice);
  }

  const existingTKDService = installationPoint.value?.services.sort(
    (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
  )[0];

  if (
    existingTKDService &&
    existingTKDService.deviceService?.type ===
      DeviceServiceTypeEnum.NO_SERVICE_POSSIBLE
  ) {
    TKDService.value = existingTKDService;
    isExchangePossible.value = false;
    return;
  }

  initService();
  TKDService.value.setNoTKDPossible(true);
});

const setNoExchangePossible = (isExchangePossible: boolean) => {
  if (!isExchangePossible && TKDService.value.deviceService) {
    TKDService.value.setNoTKDPossible(false);
    return;
  }

  TKDService.value.setNoTKDPossible(true);
};

watch(
  () => isExchangePossible.value,
  (isExchangePossible) => {
    setNoExchangePossible(isExchangePossible);
  }
);

const initNewDevice = (
  deviceType: DeviceType,
  installationPoint: InstallationPoint
) => {
  initDevice(deviceType);
  initAccessories();
  initOldDevice(deviceType, installationPoint);
};

const handleSubmit = () => {
  if (device.value?.isLegacyDevice() && deviceEvent.value?.counter) {
    installationPoint.value?.recaptureDevice(
      device.value,
      deviceEvent.value.counter
    );
    updateNewDevice();
  } else {
    const emitsList = handleSubmitTkdService();
    emit('update-entities', emitsList);
  }

  if (formComponentRef.value?.saveImage) {
    if (installationPoint.value) {
      formComponentRef.value?.saveImage(installationPoint.value.id);
    }
  }

  if (noMountingSelectorRef.value?.saveImage && !isExchangePossible.value) {
    noMountingSelectorRef.value?.saveImage(TKDService.value.id);
  }
};

type FormComponentRef = {
  isFormValid: boolean;
  hasImage: boolean;
  handleSubmit: () => void;
  saveImage: (id: string) => void;
  saveRadiator: () => void;
};
type OldFormComponentRef = {
  isFormValid: boolean;
};
type DeviceVerificationFormRef = {
  isFormValid: Ref<boolean>;
};
type NoMountingSelectorRef = {
  isFormValid: Ref<boolean>;
  saveImage: (id: string) => void;
};

const formComponentRef: Ref<FormComponentRef | null> = ref(null);
const oldFormComponentRef: Ref<OldFormComponentRef | null> = ref(null);
const deviceVerificationFormRef: Ref<DeviceVerificationFormRef | null> =
  ref(null);
const noMountingSelectorRef: Ref<NoMountingSelectorRef | null> = ref(null);

const isReplacementFormValid = computed(
  () => oldFormComponentRef.value?.isFormValid !== false
);

const isDeviceVerificationValid = computed(() => {
  if (!deviceVerificationFormRef.value) return true;
  return !!unref(deviceVerificationFormRef.value.isFormValid);
});

const isNewDeviceFormValid = computed(() => {
  return (
    !!unref(formComponentRef.value?.isFormValid) && isReplacementFormValid.value
  );
});

const isFormValid = computed(() => {
  if (!isExchangePossible.value) {
    return !!unref(noMountingSelectorRef.value?.isFormValid);
  }

  return isDeviceVerificationValid.value && isNewDeviceFormValid.value;
});

const isNoMountingFormValid = computed(() => {
  return !!unref(noMountingSelectorRef.value?.isFormValid);
});

defineExpose({
  isFormValid,
  handleSubmit,
});

provide('isNoMountingFormValid', isNoMountingFormValid);
provide('failedInstallationAttempt', failedInstallationAttempt);
provide('oldDevice', oldDevice);
provide('oldDeviceEvent', oldDeviceEvent);
</script>
